import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { useSession } from '../../lib/useSession';
import { describeApiError } from './errors';
import { itemsOf, useApiQuery } from './hooks';
import { permissionsFor } from './permissions';
import { QrFiltersBar } from './QrFiltersBar';
import { QrTable } from './QrTable';
import { formatDate } from './states';
import type { Lot, Paged, QrFilters, QrSummary } from './types';
import { useMembers } from './useMemberNames';
import { useOrgApi } from './useOrgApi';
import { useQrList } from './useQrList';

const createLinkCls = 'inline-flex items-center rounded-md bg-action px-4 py-2 text-sm font-semibold text-ink no-underline hover:bg-action-hover';

/**
 * Aviso al partner cuando el operador le quitó un código (R4.2). Se lee de los QR dados de baja que
 * traen `unlinked`; no hay endpoint aparte.
 */
function UnlinkedNotice({ items }: { items: QrSummary[] }) {
  if (items.length === 0) return null;
  const one = items.length === 1 ? items[0] : null;
  return (
    <Banner tone="warning">
      <p>
        {one ? (
          <>
            El operador de q-r.cl quitó el código <span className="font-mono font-semibold">{one.code}</span> a tu organización
            {one.unlinked?.at ? ` el ${formatDate(one.unlinked.at)}` : ''}. Dejó de redirigir y no se puede reasignar durante su cuarentena.
          </>
        ) : (
          <>El operador de q-r.cl quitó {items.length} códigos a tu organización. Dejaron de redirigir y quedaron en cuarentena.</>
        )}
      </p>
      <ul className="mt-1 space-y-0.5 text-xs">
        {items.map((qr) => (
          <li key={qr.id ?? qr.code}>
            <span className="font-mono font-semibold">{qr.code}</span>
            {qr.unlinked?.reason ? ` · motivo: «${qr.unlinked.reason}»` : ''}
            {qr.unlinked?.quarantineUntil ? ` · en cuarentena hasta el ${formatDate(qr.unlinked.quarantineUntil)}` : ''}
          </li>
        ))}
      </ul>
    </Banner>
  );
}

export function QrsPage() {
  const client = useOrgApi();
  const { activeOrg } = useSession();
  const perms = permissionsFor(activeOrg?.role);
  const [filters, setFilters] = useState<QrFilters>({ state: 'activado' });
  const list = useQrList(client, filters);
  const lots = useApiQuery((signal) => client.get<{ items: Lot[] }>('/v1/lots', { signal }).then(itemsOf).catch(() => [] as Lot[]), [client]);
  const { members, names } = useMembers(client, perms.manageLots);
  const unlinked = useApiQuery(
    (signal) =>
      client
        .get<Paged<QrSummary>>('/v1/qrs?state=dado_de_baja', { signal })
        .then((res) => itemsOf(res).filter((qr) => qr.unlinked))
        .catch(() => [] as QrSummary[]),
    [client],
  );
  const filtered = Boolean(filters.q || filters.kind || filters.lotId || filters.custodianUid || (filters.state && filters.state !== 'activado'));

  return (
    <section className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-xl font-bold text-ink">QR activados</h1>
          <p className="text-sm text-ink-muted">Físicos de tus lotes y QR digitales: clásicos, con estilo y QR Art.</p>
        </div>
        {perms.createQr && (
          <Link to="/consola/qr/nuevo" className={createLinkCls}>
            Crear QR
          </Link>
        )}
      </div>
      <div aria-live="polite">
        <UnlinkedNotice items={unlinked.data ?? []} />
      </div>
      <QrFiltersBar value={filters} onChange={setFilters} lots={lots.data} custodians={members} />
      {list.error ? (
        <Banner tone="error" action={<Button size="sm" variant="secondary" onClick={list.reload}>Reintentar</Button>}>
          {describeApiError(list.error, 'No pudimos cargar los QR.')}
        </Banner>
      ) : !list.loading && (list.data?.items ?? []).length === 0 && !filtered ? (
        <EmptyState
          title="Todavía no hay QR activados"
          description="Activa calcomanías en terreno o crea un QR digital: clásico, con el estilo de tu marca o QR Art."
          action={perms.createQr ? <Link to="/consola/qr/nuevo" className={createLinkCls}>Crear QR</Link> : undefined}
        />
      ) : (
        <QrTable
          caption="QR de la organización"
          loading={list.loading}
          rows={list.data?.items ?? []}
          columns={['thumb', 'code', 'type', 'business', 'ref', 'target', 'state', 'scans', 'activatedBy', 'actions']}
          names={names}
          empty="Ningún QR calza con los filtros."
        />
      )}
    </section>
  );
}
